import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "destructive";
type Size = "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const base =
  "inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition " +
  "disabled:opacity-50 disabled:cursor-not-allowed select-none active:scale-[0.98]";

const variants: Record<Variant, string> = {
  primary:
    "bg-primary text-primary-fg hover:bg-primary-hover shadow-[0_1px_2px_rgba(15,23,42,0.08),0_1px_0_rgba(255,255,255,0.18)_inset]",
  secondary: "border border-border bg-surface text-text hover:bg-surface-2 hover:border-border-strong",
  ghost: "text-text hover:bg-surface-2",
  destructive: "bg-danger text-white hover:opacity-95",
};

const sizes: Record<Size, string> = {
  md: "h-11 px-4 text-[15px] min-w-[44px]",
  lg: "h-12 px-5 text-base min-w-[44px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(base, variants[variant], sizes[size], className)}
      {...rest}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      className="inline-block h-4 w-4 rounded-full border-2 border-current border-r-transparent animate-spin"
      aria-hidden
    />
  );
}
